import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';

import { ReactComponent as NavigateArrow } from '../../../../assets/icons/dashboard/navigate-arrow.svg';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import CustomToggleButton from '../../../../shared-components/custom-toggle-button/CustomToggleButton';
import { TooltipIcon } from '../../../../shared-components/tooltip/TooltipIcon';
import dashboard from '../../../../utils/api/dashboard';

/**
 * Returns color based on status
 * @param {string} status The performance status.
 * @param {any} value - The value
 * @returns {string} The color hex code.
 */
const getBarColor = (status: string, value: number) => {
  if (value === 0) return '#E2E8F0'; // Light gray for 0 values
  switch (status) {
    case 'optimal':
      return '#2E9E3A';
    case 'subOptimal':
      return '#F79009';
    case 'critical':
      return '#EF4444';
    default:
      return '#2E9E3A';
  }
};

/**
 * Skeleton Chart sub-component for empty data state
 */
function SkeletonChart() {
  const skeletonData = Array.from({ length: 8 }).map((_, index) => ({
    name: `Loading ${index}`,
    value: 100,
  }));

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={skeletonData}
        layout="vertical"
        margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
        barSize={20}>
        <XAxis type="number" hide domain={[0, 100]} />
        <YAxis
          dataKey="name"
          type="category"
          axisLine={false}
          tickLine={false}
          tick={false}
          width={80}
        />
        <Bar dataKey="value" radius={[0, 4, 4, 0]} fill="#F1F5F9" isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/**
 * Health Score Chart sub-component
 * @param {any} param0 - The param0
 * @param {any} param0.data - The data prop
 */
function HealthScoreChart({ data }: { data: any[] }) {
  /**
   * Custom tooltip for the bar chart
   * @param {any} param0 - The param0
   * @param {any} param0.active - The active prop
   * @param {any} param0.payload - The payload prop
   */
  function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
    if (active && payload && payload.length > 0) {
      const entry = payload[0]?.payload;
      return (
        <div
          style={{
            background: '#fff',
            border: '1px solid #E2E8F0',
            borderRadius: '8px',
            padding: '8px 14px',
            fontSize: '13px',
            color: '#334155',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}>
          <p style={{ fontWeight: 600, marginBottom: 4 }}>{entry?.name}</p>
          <p style={{ color: getBarColor(entry?.status, entry?.value) }}>{entry?.value}</p>
        </div>
      );
    }
    return null;
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
        barSize={20}>
        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
        <XAxis type="number" hide />
        <YAxis
          dataKey="name"
          type="category"
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 13, fill: '#64748B' }}
          width={80}
        />
        <Bar dataKey="renderValue" radius={[0, 4, 4, 0]}>
          {data.map((entry, index) => (
            <Cell key={`hs-cell-${index}`} fill={getBarColor(entry.status, entry.value)} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/**
 * Output Chart sub-component
 * @param {any} param0 - The param0
 * @param {any} param0.data - The data prop
 */
function OutputChart({ data }: { data: any[] }) {
  /**
   * Custom tooltip for the bar chart
   * @param {any} param0 - The param0
   * @param {any} param0.active - The active prop
   * @param {any} param0.payload - The payload prop
   */
  function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
    if (active && payload && payload.length > 0) {
      const entry = payload[0]?.payload;
      return (
        <div
          style={{
            background: '#fff',
            border: '1px solid #E2E8F0',
            borderRadius: '8px',
            padding: '8px 14px',
            fontSize: '13px',
            color: '#334155',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}>
          <p style={{ fontWeight: 600, marginBottom: 4 }}>{entry?.name}</p>
          <p style={{ color: '#2E9E3A' }}>{`${entry?.value} kWh`}</p>
        </div>
      );
    }
    return null;
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
        barSize={20}>
        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
        <XAxis type="number" hide />
        <YAxis
          dataKey="name"
          type="category"
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 13, fill: '#64748B' }}
          width={80}
        />
        <Bar dataKey="renderValue" radius={[0, 4, 4, 0]}>
          {data.map((entry, index) => (
            <Cell key={`out-cell-${index}`} fill={entry.value === 0 ? '#E2E8F0' : '#2E9E3A'} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

interface NewStationPerformanceProperties {
  filters?: {
    facilityIds: string[];
    stationIds: string[];
    deviceIds: string[];
    type: string[];
  };
}

/**
 * NewStationPerformanceChart component
 * Displays a horizontal bar chart for station health/output (Redesigned Version)
 * @param {any} param0 - The param0
 * @param {any} param0.filters - The filters prop
 */
function NewStationPerformanceChart({ filters }: NewStationPerformanceProperties) {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<'healthScore' | 'output'>('healthScore');
  const [graphData, setGraphData] = useState<any[]>([]);

  /**
   * Fetches chart data based on the active tab and filters.
   */
  const fetchChartData = async () => {
    setIsLoading(true);
    try {
      const query = {
        accountCustomId: filters?.facilityIds,
        stationIds: filters?.stationIds,
        hardRefresh: true,
      };
      let response;

      if (activeTab === 'healthScore') {
        response = await dashboard.getStationHealthScore({ ...query, type: 'healthScore' });
      } else {
        response = await dashboard.getStationHealthScore({
          ...query,
          type: 'totalUsagePowerConsume',
        });
      }

      if (response?.status === 200 && Array.isArray(response?.data?.results)) {
        const mappedData = response.data?.results?.map((item: any) => {
          const rawValue =
            activeTab === 'healthScore'
              ? (item.healthScore ?? item.value ?? 0)
              : (item.performanceScore ?? item.value ?? 0);
          return {
            name: item.stationName || item.name || '',
            value: rawValue,
            renderValue: rawValue === 0 ? 2 : rawValue, // Small block for 0 values
            status: item.status || 'optimal',
          };
        });
        setGraphData(mappedData);
      } else {
        setGraphData([]);
      }
    } catch (error) {
      console.error('Error fetching station performance data:', error);
      setGraphData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchChartData();
  }, [filters, activeTab]);

  // Calculate dynamic height based on number of bars (approx 45px per bar)
  const chartHeight = Math.max(350, graphData.length * 45);

  return (
    <div className="new-station-performance-chart-wrap">
      <div className="new-station-performance-chart-header">
        <div className="header-top">
          <div className="header-title">
            <div className="title-wrapper">
              <h6>{t('stationPerformance')}</h6>
              <TooltipIcon text="Station performance chart" infoColor="info" />
            </div>
            <NavigateArrow
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('/ev-admin/operations/charging-station/approved')}
            />
          </div>
        </div>

        <div className="header-bottom">
          <div className="chart-legend">
            {activeTab === 'healthScore' && (
              <>
                <div className="legend-item">
                  <span className="dot optimal" />
                  <span>{t('optimal')}</span>
                </div>
                <div className="legend-item">
                  <span className="dot sub-optimal" />
                  <span>{t('subOptimal')}</span>
                </div>
                <div className="legend-item">
                  <span className="dot critical" />
                  <span>{t('critical')}</span>
                </div>
              </>
            )}
          </div>

          <div className="toggle-container">
            <CustomToggleButton
              labels={[t('healthScore'), t('output')]}
              defaultActiveIndex={0}
              onToggle={(index) => setActiveTab(index === 0 ? 'healthScore' : 'output')}
              type="chart"
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <center className="station-performance-loader">
          <BatteryLoader />
        </center>
      ) : (
        <div className="station-performance-chart-scroll-container">
          <div className="station-performance-chart-container" style={{ height: chartHeight }}>
            {graphData.length === 0 && <SkeletonChart />}
            {graphData.length > 0 && activeTab === 'healthScore' && (
              <HealthScoreChart data={graphData} />
            )}
            {graphData.length > 0 && activeTab === 'output' && <OutputChart data={graphData} />}
          </div>
        </div>
      )}
    </div>
  );
}

NewStationPerformanceChart.defaultProps = {
  filters: {
    facilityIds: [],
    stationIds: [],
    deviceIds: [],
    type: ['commercial'],
  },
};
export default NewStationPerformanceChart;
